import axios from 'axios';
import type {
  Token,
  User,
  ReportGenerateRequest,
  ReportGenerateResponse,
  ClassifyResponse,
  Report,
  Preferences,
  PreferencesUpdate,
  ReportTemplate,
  TemplateCreate,
  TemplateUpdate,
  ExamCategory,
  ReportProfile,
  EffectiveConfig,
  ProfileUpsert,
  Feedback,
  FeedbackCreate,
  FeedbackUpdate,
  FeedbackStatus,
  FeedbackCategory,
  BacklogItem,
  BacklogStatus,
  BacklogPriority,
  Feature,
  FeatureStatus,
  FeatureDetail,
  UatCase,
  LineageEvent,
  StyleRule,
  StyleRuleState,
  TemplateProposal,
  TemplateProposalStatus,
  LearningMetrics,
  BanditMetrics,
} from '../types';

// Determine API URL:
// 1. Explicit env var (not "auto") -> use it directly
// 2. Production hostname -> use the API subdomain
// 3. Otherwise -> same hostname, port 8010 (works for localhost + LAN)
function resolveApiUrl(): string {
  const env = import.meta.env.VITE_API_URL;
  if (env && env !== 'auto') return env;
  const host = window.location.hostname;
  if (host === 'radiology.axiomdata.com.au') {
    return 'https://api-radiology.axiomdata.com.au';
  }
  return `http://${host}:8010`;
}
const API_URL = resolveApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: async (email: string, password: string, fullName: string): Promise<Token> => {
    const response = await api.post('/api/auth/register', {
      email,
      password,
      full_name: fullName,
    });
    return response.data;
  },

  login: async (email: string, password: string): Promise<Token> => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const response = await api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getMe: async (): Promise<User> => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  refresh: async (): Promise<Token> => {
    const response = await api.post('/api/auth/refresh');
    return response.data;
  },
};

// Transcription API
export const transcriptionApi = {
  upload: async (audioBlob: Blob): Promise<{ transcription: string; language: string; duration: number }> => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.webm');

    const response = await api.post('/api/transcription/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Reports API
export const reportsApi = {
  generate: async (request: ReportGenerateRequest): Promise<ReportGenerateResponse> => {
    const response = await api.post('/api/reports/generate', request);
    return response.data;
  },

  // Pass 1: recommend a template for the confirmation gate.
  classify: async (transcription: string): Promise<ClassifyResponse> => {
    const response = await api.post('/api/reports/classify', { transcription });
    return response.data;
  },

  generateStream: async (
    request: ReportGenerateRequest,
    onChunk: (text: string) => void,
    onDone: () => void,
    onError: (error: string) => void
  ): Promise<void> => {
    const token = localStorage.getItem('token');

    const response = await fetch(`${API_URL}/api/reports/generate/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Stream request failed');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.text) {
              onChunk(data.text);
            }
            if (data.done) {
              onDone();
            }
            if (data.error) {
              onError(data.error);
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
  },

  create: async (report: Partial<Report>): Promise<Report> => {
    const response = await api.post('/api/reports/', report);
    return response.data;
  },

  list: async (params?: { modality?: string; patient_identifier?: string }): Promise<Report[]> => {
    const response = await api.get('/api/reports/', { params });
    return response.data;
  },

  get: async (id: string): Promise<Report> => {
    const response = await api.get(`/api/reports/${id}`);
    return response.data;
  },

  update: async (id: string, data: Partial<Report>): Promise<Report> => {
    const response = await api.put(`/api/reports/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/reports/${id}`);
  },
};

// Preferences API
export const preferencesApi = {
  get: async (): Promise<Preferences> => {
    const response = await api.get('/api/preferences/');
    return response.data;
  },

  update: async (data: PreferencesUpdate): Promise<Preferences> => {
    const response = await api.put('/api/preferences/', data);
    return response.data;
  },
};

// Export API
export const exportApi = {
  rtf: async (report: string, metadata?: Record<string, string>): Promise<Blob> => {
    const response = await api.post(
      '/api/export/rtf',
      { report, ...metadata },
      { responseType: 'blob' }
    );
    return response.data;
  },

  hl7: async (report: string, metadata?: Record<string, string>): Promise<Blob> => {
    const response = await api.post(
      '/api/export/hl7',
      { report, ...metadata },
      { responseType: 'blob' }
    );
    return response.data;
  },

  text: async (report: string, metadata?: Record<string, string>): Promise<Blob> => {
    const response = await api.post(
      '/api/export/text',
      { report, ...metadata },
      { responseType: 'blob' }
    );
    return response.data;
  },

  clipboard: async (report: string, decisionId?: string): Promise<{ text: string; rtf?: string }> => {
    const response = await api.post('/api/export/clipboard', {
      report,
      decision_id: decisionId || undefined,
    });
    return response.data;
  },
};

// Template API
export const templateApi = {
  list: async (params?: { exam_category?: string; modality?: string }): Promise<ReportTemplate[]> => {
    const response = await api.get('/api/templates', { params });
    return response.data;
  },

  get: async (id: string): Promise<ReportTemplate> => {
    const response = await api.get(`/api/templates/${id}`);
    return response.data;
  },

  categories: async (): Promise<ExamCategory[]> => {
    const response = await api.get('/api/templates/categories');
    return response.data;
  },

  create: async (data: TemplateCreate): Promise<ReportTemplate> => {
    const response = await api.post('/api/templates', data);
    return response.data;
  },

  update: async (id: string, data: TemplateUpdate): Promise<ReportTemplate> => {
    const response = await api.put(`/api/templates/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/templates/${id}`);
  },

  clone: async (id: string): Promise<ReportTemplate> => {
    const response = await api.post(`/api/templates/${id}/clone`);
    return response.data;
  },

  recordSignal: async (decisionId: string, signalType: string): Promise<void> => {
    await api.post(`/api/templates/decisions/${decisionId}/signal`, {
      signal_type: signalType,
    });
  },
};

// Report Profile API (onboarding repository)
export const profilesApi = {
  list: async (): Promise<ReportProfile[]> => {
    const response = await api.get('/api/profiles');
    return response.data;
  },

  getEffective: async (examCategory: string): Promise<EffectiveConfig> => {
    const response = await api.get(`/api/profiles/${examCategory}/effective`);
    return response.data;
  },

  upsert: async (examCategory: string, data: ProfileUpsert): Promise<ReportProfile> => {
    const response = await api.put(`/api/profiles/${examCategory}`, data);
    return response.data;
  },

  remove: async (examCategory: string): Promise<void> => {
    await api.delete(`/api/profiles/${examCategory}`);
  },
};

// Edit Tracking API
export const editTrackingApi = {
  record: async (data: {
    generated_report: string;
    final_report: string;
    transcription?: string;
    decision_id?: string;
    modality?: string;
    body_part?: string;
    exam_category?: string;
    template_id?: string;
    template_name?: string;
    model_used?: string;
    trigger: string;
  }): Promise<{ id: string; has_edits: boolean; edit_distance: number }> => {
    const response = await api.post('/api/report-edits', data);
    return response.data;
  },

  stats: async (days = 30) => {
    const response = await api.get('/api/report-edits/stats', { params: { days } });
    return response.data;
  },

  list: async (params: {
    page?: number;
    per_page?: number;
    has_edits?: boolean;
    exam_category?: string;
    days?: number;
  } = {}) => {
    const response = await api.get('/api/report-edits', { params });
    return response.data;
  },

  detail: async (id: string) => {
    const response = await api.get(`/api/report-edits/${id}`);
    return response.data;
  },
};

// Analytics API
export const analyticsApi = {
  summary: async (days = 30) => {
    const response = await api.get('/api/analytics/usage/summary', { params: { days } });
    return response.data;
  },

  byModel: async (days = 30) => {
    const response = await api.get('/api/analytics/usage/by-model', { params: { days } });
    return response.data;
  },

  daily: async (days = 30) => {
    const response = await api.get('/api/analytics/usage/daily', { params: { days } });
    return response.data;
  },

  recent: async (eventType?: string, limit = 50) => {
    const params: Record<string, string | number> = { limit };
    if (eventType) params.event_type = eventType;
    const response = await api.get('/api/analytics/usage/recent', { params });
    return response.data;
  },

  healthDetailed: async () => {
    const response = await api.get('/health/detailed');
    return response.data;
  },

  // API cost analytics (admin)
  costSummary: async (days = 30) =>
    (await api.get('/api/analytics/cost/summary', { params: { days } })).data,
  costBurnRate: async (days = 14) =>
    (await api.get('/api/analytics/cost/burn-rate', { params: { days } })).data,
  costByModel: async (days = 30) =>
    (await api.get('/api/analytics/cost/by-model', { params: { days } })).data,
  costByOperation: async (days = 30) =>
    (await api.get('/api/analytics/cost/by-operation', { params: { days } })).data,
  costByEnvironment: async (days = 30) =>
    (await api.get('/api/analytics/cost/by-environment', { params: { days } })).data,
  costDaily: async (days = 30) =>
    (await api.get('/api/analytics/cost/daily', { params: { days } })).data,

  history: async (params: {
    page?: number;
    per_page?: number;
    event_type?: string;
    model?: string;
    status?: string;
    search?: string;
    days?: number;
  } = {}) => {
    const response = await api.get('/api/analytics/usage/history', { params });
    return response.data;
  },

  classification: async (days = 30) => {
    const response = await api.get('/api/analytics/classification', { params: { days } });
    return response.data;
  },
};

// Feedback API
export const feedbackApi = {
  submit: async (payload: FeedbackCreate): Promise<{ id: string; status: FeedbackStatus }> => {
    const response = await api.post('/api/feedback', payload);
    return response.data;
  },

  list: async (params: { status?: FeedbackStatus; category?: FeedbackCategory; limit?: number } = {}): Promise<Feedback[]> => {
    const response = await api.get('/api/feedback', { params });
    return response.data;
  },

  get: async (id: string): Promise<Feedback> => {
    const response = await api.get(`/api/feedback/${id}`);
    return response.data;
  },

  update: async (id: string, payload: FeedbackUpdate): Promise<Feedback> => {
    const response = await api.patch(`/api/feedback/${id}`, payload);
    return response.data;
  },

  screenshotUrl: (id: string): string => {
    // Returns a URL the admin UI can load directly into an <img>; auth header is
    // injected via fetch since <img> can't carry one. Use fetchScreenshot for display.
    return `${API_URL}/api/feedback/${id}/screenshot`;
  },

  fetchScreenshot: async (id: string): Promise<string> => {
    // Returns an object URL the UI can stick into <img src=...>
    const response = await api.get(`/api/feedback/${id}/screenshot`, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  },

  signalCounts: async (days = 30): Promise<{ days: number; counts: Record<string, number>; total: number }> => {
    const response = await api.get('/api/feedback/signals/counts', { params: { days } });
    return response.data;
  },
};

// Workshop API — admin portal: backlog, features, UAT, lineage
export const workshopApi = {
  // Features
  listFeatures: async (params: { status?: FeatureStatus } = {}): Promise<Feature[]> => {
    const response = await api.get('/api/workshop/features', { params });
    return response.data;
  },
  createFeature: async (payload: Partial<Feature> & { name: string; slug: string }): Promise<Feature> => {
    const response = await api.post('/api/workshop/features', payload);
    return response.data;
  },
  getFeature: async (id: string): Promise<FeatureDetail> => {
    const response = await api.get(`/api/workshop/features/${id}`);
    return response.data;
  },
  updateFeature: async (id: string, payload: Partial<Feature>): Promise<Feature> => {
    const response = await api.patch(`/api/workshop/features/${id}`, payload);
    return response.data;
  },
  deleteFeature: async (id: string): Promise<void> => {
    await api.delete(`/api/workshop/features/${id}`);
  },

  // Backlog items
  listItems: async (params: { status?: BacklogStatus; priority?: BacklogPriority; feature_id?: string; unassigned?: boolean } = {}): Promise<BacklogItem[]> => {
    const response = await api.get('/api/workshop/items', { params });
    return response.data;
  },
  createItem: async (payload: { title: string; description?: string; priority?: BacklogPriority; source_type?: string; source_id?: string; feature_id?: string }): Promise<BacklogItem> => {
    const response = await api.post('/api/workshop/items', payload);
    return response.data;
  },
  updateItem: async (id: string, payload: Partial<BacklogItem>): Promise<BacklogItem> => {
    const response = await api.patch(`/api/workshop/items/${id}`, payload);
    return response.data;
  },
  deleteItem: async (id: string): Promise<void> => {
    await api.delete(`/api/workshop/items/${id}`);
  },
  promoteFromFeedback: async (payload: { feedback_id: string; title?: string; priority?: BacklogPriority; feature_id?: string }): Promise<BacklogItem> => {
    const response = await api.post('/api/workshop/items/promote', payload);
    return response.data;
  },

  // UAT
  createUat: async (payload: { feature_id: string; title: string; steps?: string; expected_result?: string }): Promise<UatCase> => {
    const response = await api.post('/api/workshop/uat', payload);
    return response.data;
  },
  updateUat: async (id: string, payload: Partial<UatCase>): Promise<UatCase> => {
    const response = await api.patch(`/api/workshop/uat/${id}`, payload);
    return response.data;
  },
  deleteUat: async (id: string): Promise<void> => {
    await api.delete(`/api/workshop/uat/${id}`);
  },

  // Lineage
  listLineage: async (params: { limit?: number; entity_type?: string } = {}): Promise<LineageEvent[]> => {
    const response = await api.get('/api/workshop/lineage', { params });
    return response.data;
  },

  // Learning engine
  listStyleRules: async (params: { user_id?: string; exam_category?: string; state?: StyleRuleState; limit?: number } = {}): Promise<StyleRule[]> => {
    const response = await api.get('/api/workshop/style-rules', { params });
    return response.data;
  },
  listTemplateProposals: async (params: { status?: TemplateProposalStatus; user_id?: string } = {}): Promise<TemplateProposal[]> => {
    const response = await api.get('/api/workshop/template-proposals', { params });
    return response.data;
  },
  approveTemplateProposal: async (id: string, notes?: string): Promise<{ id: string; status: string }> => {
    const response = await api.post(`/api/workshop/template-proposals/${id}/approve`, null, { params: notes ? { notes } : {} });
    return response.data;
  },
  rejectTemplateProposal: async (id: string, notes?: string): Promise<{ id: string; status: string }> => {
    const response = await api.post(`/api/workshop/template-proposals/${id}/reject`, null, { params: notes ? { notes } : {} });
    return response.data;
  },
  getTemplateProposalRaw: async (id: string): Promise<{ raw_llm_response: string }> => {
    const response = await api.get(`/api/workshop/template-proposals/${id}/raw`);
    return response.data;
  },
  learningMetrics: async (days = 60): Promise<LearningMetrics> => {
    const response = await api.get('/api/workshop/learning-metrics', { params: { days } });
    return response.data;
  },
  banditMetrics: async (): Promise<BanditMetrics> => {
    const response = await api.get('/api/workshop/bandit-metrics');
    return response.data;
  },
};

// Admin customer-management API (admin role only)
export interface AdminCustomer {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  free_reports_remaining: number | null;
  llm_model: string | null;
  created_at: string;
  generations: number;
  transcriptions: number;
  failed_events: number;
  error_signals: number;
  cost_usd: number;
  last_active: string | null;
}

export interface AdminCustomerUpdate {
  full_name?: string;
  role?: string;
  is_active?: boolean;
  free_reports_remaining?: number | null;
  llm_model?: string | null;
}

export const adminApi = {
  customers: async (days = 30): Promise<{ days: number; customers: AdminCustomer[] }> =>
    (await api.get('/api/admin/customers', { params: { days } })).data,

  customerDetail: async (userId: string, days = 30) =>
    (await api.get(`/api/admin/customers/${userId}`, { params: { days } })).data,

  updateCustomer: async (userId: string, payload: AdminCustomerUpdate) =>
    (await api.patch(`/api/admin/customers/${userId}`, payload)).data,

  customerActivity: async (userId: string, opts: { limit?: number; errors_only?: boolean } = {}) =>
    (await api.get(`/api/admin/customers/${userId}/activity`, { params: opts })).data,

  customerErrors: async (userId: string, days = 30) =>
    (await api.get(`/api/admin/customers/${userId}/errors`, { params: { days } })).data,

  customerCosts: async (userId: string, days = 30) =>
    (await api.get(`/api/admin/customers/${userId}/costs`, { params: { days } })).data,
};

export const adminLearningApi = {
  health: async () =>
    (await api.get('/api/admin/learning/health')).data,

  evolution: async (days = 90) =>
    (await api.get('/api/admin/learning/evolution', { params: { days } })).data,

  categories: async (days = 90, userId?: string) =>
    (await api.get('/api/admin/learning/categories', { params: { days, ...(userId ? { user_id: userId } : {}) } })).data,

  generations: async (opts: { limit?: number; exam_category?: string; user_id?: string } = {}) =>
    (await api.get('/api/admin/learning/generations', { params: opts })).data,

  rules: async (opts: { state?: string; exam_category?: string; user_id?: string; limit?: number } = {}) =>
    (await api.get('/api/admin/learning/rules', { params: opts })).data,

  activateRule: async (ruleId: string) =>
    (await api.post(`/api/admin/learning/rules/${ruleId}/activate`)).data,

  dismissRule: async (ruleId: string) =>
    (await api.post(`/api/admin/learning/rules/${ruleId}/dismiss`)).data,

  restoreRule: async (ruleId: string) =>
    (await api.post(`/api/admin/learning/rules/${ruleId}/restore`)).data,
};

export default api;
